import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { IconType } from '@factorialco/f0-react'
import { Menu } from '@factorialco/f0-react/dist/experimental'
import {
  FileSigned,
  Money,
  MoneyBag,
  People,
  Rocket,
  Wallet,
} from '@factorialco/f0-react/icons/app'
import { ExpenseDetailPageQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import Accounting from 'modules/expenses/pages/settings/ExpensePolicy/Accounting'
import DigitalCertifiedDocuments from 'modules/expenses/pages/settings/ExpensePolicy/DigitalCertifiedDocuments'
import DiscoverMore from 'modules/expenses/pages/settings/ExpensePolicy/DiscoverMore'
import ExpensesApprovalWorkflow from 'modules/expenses/pages/settings/ExpensePolicy/ExpensesApprovalWorkflow'
import FormsRequirements from 'modules/expenses/pages/settings/ExpensePolicy/FormsRequirements'
import Rates from 'modules/expenses/pages/settings/ExpensePolicy/Rates'
import SpendingGroups from 'modules/expenses/pages/settings/ExpensePolicy/SpendingGroups'

type ExpensesSignedDocumentsTerm = NonNullable<
  NonNullable<ExpenseDetailPageQuery['expenses']['signedDocumentsTermsConnection']>['nodes']
>[number]

type MenuItemConfig = {
  id: string
  label: string
  icon: IconType
}

type MenuSectionConfig = {
  id: string
  title: string
  items: MenuItemConfig[]
}

interface SectionContentProps {
  hasPerDiemsFeature: boolean
  hasNewPerDiemFeature: boolean
  hasExpensesGroupsFeature: boolean
  isExpensesGroupsEnabled: boolean
  hasLedgerAccountFeature: boolean
  canSeeLedgerAccounts: boolean
  signedDocumentsTerms: ExpensesSignedDocumentsTerm[]
  hasReportOnBehalfFeature: boolean
}

const SectionContent = ({
  selectedItemId,
  ...props
}: SectionContentProps & { selectedItemId: string }) => {
  switch (selectedItemId) {
    case 'approval_workflows':
      return <ExpensesApprovalWorkflow />
    case 'forms_requirements':
      return <FormsRequirements hasPerDiemsFeature={props.hasPerDiemsFeature} />
    case 'rates':
      return (
        <Rates
          hasPerDiemsFeature={props.hasPerDiemsFeature}
          hasNewPerDiemFeature={props.hasNewPerDiemFeature}
        />
      )
    case 'spending_groups':
      return (
        <SpendingGroups
          hasExpensesGroupsFeature={props.hasExpensesGroupsFeature}
          isExpensesGroupsEnabled={props.isExpensesGroupsEnabled}
        />
      )
    case 'digital_certified_documents':
      return <DigitalCertifiedDocuments signedDocumentsTerms={props.signedDocumentsTerms} />
    case 'accounting':
      return (
        <Accounting
          hasLedgerAccountFeature={props.hasLedgerAccountFeature}
          canSeeLedgerAccounts={props.canSeeLedgerAccounts}
        />
      )
    case 'discover_more':
      return <DiscoverMore hasReportOnBehalfFeature={props.hasReportOnBehalfFeature} />
    default:
      return null
  }
}

interface Props extends SectionContentProps {
  hasExpenseApprovalWorkflowsFeature: boolean
}

const ExpensePolicy = ({ hasExpenseApprovalWorkflowsFeature, ...sectionContentProps }: Props) => {
  const [selectedItemId, setSelectedItemId] = useState(
    hasExpenseApprovalWorkflowsFeature ? 'approval_workflows' : 'forms_requirements'
  )

  const menuSections = useMemo<MenuSectionConfig[]>(() => {
    const generalSettingsItems: MenuItemConfig[] = []

    if (hasExpenseApprovalWorkflowsFeature) {
      generalSettingsItems.push({
        id: 'approval_workflows',
        label: i18n.t('expenses_v2.settings.expense_policy.approval_workflows'),
        icon: Wallet,
      })
    }

    // TODO: cards
    generalSettingsItems.push(
      {
        id: 'forms_requirements',
        label: i18n.t('expenses_v2.settings.forms.title'),
        icon: FileSigned,
      },
      { id: 'rates', label: i18n.t('expenses_v2.settings.rate_configurations.title'), icon: Money },
      {
        id: 'spending_groups',
        label: i18n.t('expenses_v2.settings.expenses_groups.title'),
        icon: People,
      }
    )

    return [
      {
        id: 'general_settings',
        title: i18n.t('expenses_v2.settings.expense_policy.general_settings'),
        items: generalSettingsItems,
      },
      // TODO: categories
      {
        id: 'other_settings',
        title: i18n.t('expenses_v2.settings.expense_policy.other_settings'),
        items: [
          {
            id: 'digital_certified_documents',
            label: i18n.t('expenses.settings.legal_compliance.title_v2'),
            icon: FileSigned,
          },
          {
            id: 'accounting',
            label: i18n.t('expenses_v2.settings.accounting.title'),
            icon: MoneyBag,
          },
          {
            id: 'discover_more',
            label: i18n.t('expenses_v2.settings.discover_more_features.title'),
            icon: Rocket,
          },
          //TODO: policy_document
        ],
      },
    ]
  }, [hasExpenseApprovalWorkflowsFeature])

  return (
    <Box flexDirection="row" height="full" width="full">
      <Box
        padding="s4"
        paddingBottom="s16"
        overflowX="hidden"
        height="full"
        border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
        borderRadius={{ all: 'abs008' }}
      >
        <Menu
          tree={menuSections.map((section) => ({
            id: section.id,
            title: section.title,
            items: section.items.map((item) => ({
              id: item.id,
              label: item.label,
              icon: item.icon,
              onClick: () => setSelectedItemId(item.id),
            })),
          }))}
        />
      </Box>
      <SectionContent selectedItemId={selectedItemId} {...sectionContentProps} />
    </Box>
  )
}

export default ExpensePolicy
